import request from './request';
import endPoint from './endPoint';

/**
 * Service de gestion des formules d'abonnement
 * Compatible avec l'API Laravel SubscriptionPlanController
 */
export class SubscriptionPlanService {
  constructor() {
    this.baseUrl = `/subscription-plans`;
  }

  /**
   * Récupérer toutes les formules d'abonnement
   */
  async getAll(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      // Paramètres de pagination
      if (params.page) queryParams.append('page', params.page);
      if (params.per_page) queryParams.append('per_page', params.per_page);
      
      // Filtres
      if (params.status) queryParams.append('status', params.status);
      if (params.price_min) queryParams.append('price_min', params.price_min);
      if (params.price_max) queryParams.append('price_max', params.price_max);
      if (params.search) queryParams.append('search', params.search);
      if (params.currency) queryParams.append('currency', params.currency);
      if (params.duration_days) queryParams.append('duration_days', params.duration_days);
      
      // Tris
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_direction) queryParams.append('sort_direction', params.sort_direction);
      
      const url = queryParams.toString() ? `${this.baseUrl}?${queryParams}` : this.baseUrl;
      const response = await request.get(url);
      
      return this.formatResponse(response);
    } catch (error) {
      console.error('Erreur lors de la récupération des formules:', error);
      throw this.formatError(error);
    }
  }

  /**
   * Récupérer une formule par son ID
   */
  async getById(id) {
    try {
      const response = await request.get(`${this.baseUrl}/${id}`);
      return this.formatResponse(response);
    } catch (error) {
      console.error(`Erreur lors de la récupération de la formule ${id}:`, error);
      throw this.formatError(error);
    }
  }

  /**
   * Créer une nouvelle formule d'abonnement
   */
  async create(planData) {
    try {
      // Validation des données
      const validationErrors = this.validatePlanData(planData);
      if (validationErrors.length > 0) {
        throw new Error(`Données invalides: ${validationErrors.join(', ')}`);
      }

      const response = await request.post(this.baseUrl, this.formatPlanData(planData));
      return this.formatResponse(response);
    } catch (error) {
      console.error('Erreur lors de la création de la formule:', error);
      throw this.formatError(error);
    }
  }

  /**
   * Mettre à jour une formule d'abonnement
   */
  async update(id, planData) {
    try {
      // Validation des données
      const validationErrors = this.validatePlanData(planData);
      if (validationErrors.length > 0) {
        throw new Error(`Données invalides: ${validationErrors.join(', ')}`);
      }

      const response = await request.put(`${this.baseUrl}/${id}`, this.formatPlanData(planData));
      return this.formatResponse(response);
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de la formule ${id}:`, error);
      throw this.formatError(error);
    }
  }

  /**
   * Supprimer une formule d'abonnement
   */
  async delete(id) {
    try {
      const response = await request.delete(`${this.baseUrl}/${id}`);
      return this.formatResponse(response);
    } catch (error) {
      console.error(`Erreur lors de la suppression de la formule ${id}:`, error);
      throw this.formatError(error);
    }
  }

  /**
   * Activer/désactiver une formule
   */
  async toggleStatus(id, isActive = true) {
    try {
      const response = await request.put(`${this.baseUrl}/${id}/status`, { 
        is_active: isActive 
      });
      return this.formatResponse(response);
    } catch (error) {
      console.error(`Erreur lors du changement de statut de la formule ${id}:`, error);
      throw this.formatError(error);
    }
  }

  /**
   * Dupliquer une formule d'abonnement
   */
  async duplicate(id, newName = null) {
    try {
      const response = await request.post(`${this.baseUrl}/${id}/duplicate`, {
        name: newName
      });
      return this.formatResponse(response);
    } catch (error) {
      console.error(`Erreur lors de la duplication de la formule ${id}:`, error);
      throw this.formatError(error);
    }
  }

  /**
   * Récupérer les formules actives uniquement
   */
  async getActive(params = {}) {
    try {
      const response = await this.getAll({
        ...params,
        status: 'active'
      });
      return response;
    } catch (error) {
      console.error('Erreur lors de la récupération des formules actives:', error);
      throw this.formatError(error);
    }
  }

  /**
   * Calculer le prix avec remise
   */
  calculateDiscountedPrice(price, discountPercentage = 0) {
    if (!price || discountPercentage <= 0) return price;
    return price * (1 - discountPercentage / 100);
  }

  /**
   * Calculer le prix total avec frais d'activation
   */
  calculateTotalPrice(price, setupFee = 0, discountPercentage = 0) {
    const discountedPrice = this.calculateDiscountedPrice(price, discountPercentage);
    return discountedPrice + (setupFee || 0);
  }

  /**
   * Formater les données de la formule pour l'API
   */
  formatPlanData(planData) {
    // Déterminer le cycle de facturation basé sur duration_days
    let billing_cycle = planData.billing_cycle || 'monthly';
    const days = parseInt(planData.duration_days) || 30;
    
    if (!planData.billing_cycle) {
      if (days === 7) {
        billing_cycle = 'weekly';
      } else if (days === 30 || days === 31) {
        billing_cycle = 'monthly';
      } else if (days === 90) {
        billing_cycle = 'quarterly';
      } else if (days === 180) {
        billing_cycle = 'semi_annual';
      } else if (days === 365 || days === 366) {
        billing_cycle = 'yearly';
      } else {
        billing_cycle = 'custom';
      }
    }

    return {
      name: planData.name,
      description: planData.description || '',
      price: parseFloat(planData.price) || 0,
      currency: planData.currency || 'XOF',
      billing_cycle: billing_cycle,
      duration_days: days,
      max_students: planData.max_students ? parseInt(planData.max_students) : -1,
      max_classes: planData.max_classes ? parseInt(planData.max_classes) : -1,
      max_subjects: planData.max_subjects ? parseInt(planData.max_subjects) : -1,
      analytics_access: Boolean(planData.analytics_access !== undefined ? planData.analytics_access : true),
      priority_support: Boolean(planData.priority_support || false),
      offline_access: Boolean(planData.offline_access !== undefined ? planData.offline_access : true),
      custom_branding: Boolean(planData.custom_branding || false),
      features: Array.isArray(planData.features) ? planData.features : [],
      restrictions: Array.isArray(planData.restrictions) ? planData.restrictions : [],
      is_active: Boolean(planData.is_active !== undefined ? planData.is_active : true),
      is_featured: Boolean(planData.is_featured || false),
      sort_order: parseInt(planData.sort_order) || 1
    };
  }

  /**
   * Validation des données de la formule
   */
  validatePlanData(data) {
    const errors = [];
    
    if (!data.name || data.name.trim().length < 3) {
      errors.push('Le nom doit contenir au moins 3 caractères');
    }
    
    if (!data.price || parseFloat(data.price) <= 0) {
      errors.push('Le prix doit être supérieur à 0');
    }
    
    if (!data.duration_days || parseInt(data.duration_days) <= 0) {
      errors.push('La durée doit être supérieure à 0 jour');
    }
    
    // Validation du cycle de facturation
    const days = parseInt(data.duration_days) || 0;
    if (days <= 0) {
      errors.push('Le cycle de facturation est obligatoire (durée en jours)');
    }
    
    // Validation des limites
    if (data.max_students !== undefined && data.max_students !== -1 && parseInt(data.max_students) <= 0) {
      errors.push('Le nombre maximum d\'élèves doit être supérieur à 0 ou -1 pour illimité');
    }
    
    if (data.max_classes !== undefined && data.max_classes !== -1 && parseInt(data.max_classes) <= 0) {
      errors.push('Le nombre maximum de classes doit être supérieur à 0 ou -1 pour illimité');
    }
    
    if (data.max_subjects !== undefined && data.max_subjects !== -1 && parseInt(data.max_subjects) <= 0) {
      errors.push('Le nombre maximum de matières doit être supérieur à 0 ou -1 pour illimité');
    }
    
    if (data.sort_order !== undefined && parseInt(data.sort_order) < 0) {
      errors.push('L\'ordre de tri ne peut pas être négatif');
    }
    
    const validCurrencies = ['XOF', 'EUR', 'USD'];
    if (data.currency && !validCurrencies.includes(data.currency)) {
      errors.push('Devise non supportée');
    }
    
    const validBillingCycles = ['weekly', 'monthly', 'quarterly', 'semi_annual', 'yearly', 'custom'];
    if (data.billing_cycle && !validBillingCycles.includes(data.billing_cycle)) {
      errors.push('Cycle de facturation non supporté');
    }
    
    return errors;
  }

  /**
   * Formater le prix avec la devise
   */
  formatPrice(price, currency = 'XOF') {
    if (!price && price !== 0) return 'N/A';
    return new Intl.NumberFormat('fr-FR').format(price) + ' ' + currency;
  }

  /**
   * Formater la durée
   */
  formatDuration(days) {
    if (!days) return 'N/A';
    
    if (days >= 365) {
      const years = Math.floor(days / 365);
      const remainingDays = days % 365;
      const months = Math.floor(remainingDays / 30);
      
      let result = `${years} an${years > 1 ? 's' : ''}`;
      if (months > 0) {
        result += ` ${months} mois`;
      }
      return result;
    } else if (days >= 30) {
      const months = Math.floor(days / 30);
      const remainingDays = days % 30;
      
      let result = `${months} mois`;
      if (remainingDays > 0) {
        result += ` ${remainingDays} jour${remainingDays > 1 ? 's' : ''}`;
      }
      return result;
    } else {
      return `${days} jour${days > 1 ? 's' : ''}`;
    }
  }

  /**
   * Obtenir les fonctionnalités par défaut
   */
  getDefaultFeatures() {
    return [
      'Accès à tous les cours',
      'Support par email',
      'Accès mobile',
      'Suivi de progression',
      'Certificats de réussite'
    ];
  }

  /**
   * Obtenir les devises supportées
   */
  getSupportedCurrencies() {
    return [
      { code: 'XOF', name: 'Franc CFA Ouest-Africain', symbol: 'XOF' },
      { code: 'EUR', name: 'Euro', symbol: '€' },
      { code: 'USD', name: 'Dollar Américain', symbol: '$' }
    ];
  }

  /**
   * Calculer les statistiques des formules
   */
  calculatePlanStats(plans) {
    if (!Array.isArray(plans)) return {};
    
    const stats = {
      total_plans: plans.length,
      active_plans: plans.filter(p => p.is_active).length,
      inactive_plans: plans.filter(p => !p.is_active).length,
      total_revenue_potential: 0,
      average_price: 0,
      price_ranges: {
        low: 0,    // 0-25000
        medium: 0, // 25000-50000
        high: 0,   // 50000-100000
        premium: 0 // 100000+
      },
      by_duration: {
        monthly: 0,    // 1-45 jours
        quarterly: 0,  // 46-135 jours
        yearly: 0      // 136+ jours
      },
      by_currency: {}
    };
    
    plans.forEach(plan => {
      const price = parseFloat(plan.price) || 0;
      stats.total_revenue_potential += price;
      
      // Répartition par prix
      if (price <= 25000) {
        stats.price_ranges.low++;
      } else if (price <= 50000) {
        stats.price_ranges.medium++;
      } else if (price <= 100000) {
        stats.price_ranges.high++;
      } else {
        stats.price_ranges.premium++;
      }
      
      // Répartition par durée
      const duration = parseInt(plan.duration_days) || 0;
      if (duration <= 45) {
        stats.by_duration.monthly++;
      } else if (duration <= 135) {
        stats.by_duration.quarterly++;
      } else {
        stats.by_duration.yearly++;
      }
      
      // Répartition par devise
      const currency = plan.currency || 'XOF';
      stats.by_currency[currency] = (stats.by_currency[currency] || 0) + 1;
    });
    
    // Prix moyen
    stats.average_price = plans.length > 0 ? 
      stats.total_revenue_potential / plans.length : 0;
    
    return stats;
  }

  /**
   * Rechercher des formules
   */
  async search(query, filters = {}) {
    try {
      const params = {
        search: query,
        ...filters
      };
      
      const response = await this.getAll(params);
      return response;
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      throw this.formatError(error);
    }
  }

  /**
   * Export des formules
   */
  async exportPlans(format = 'xlsx', filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('format', format);
      
      // Ajouter les filtres
      Object.keys(filters).forEach(key => {
        if (filters[key]) queryParams.append(key, filters[key]);
      });
      
      const response = await request.get(
        `${this.baseUrl}/export?${queryParams}`,
        { responseType: 'blob' }
      );
      
      return response;
    } catch (error) {
      console.error('Erreur lors de l\'exportation:', error);
      throw this.formatError(error);
    }
  }

  /**
   * Formater les réponses de l'API
   */
  formatResponse(response) {
    if (response.data?.data) {
      return { 
        success: true, 
        data: response.data.data,
        pagination: {
          current_page: response.data.current_page,
          last_page: response.data.last_page,
          per_page: response.data.per_page,
          total: response.data.total
        }
      };
    }
    if (response.data) {
      return { success: true, data: response.data };
    }
    return { success: true, data: response };
  }

  /**
   * Formater les erreurs
   */
  formatError(error) {
    if (error.response?.data) {
      return {
        success: false,
        message: error.response.data.message || 'Erreur inconnue',
        errors: error.response.data.errors || {},
        status: error.response.status
      };
    }
    return {
      success: false,
      message: error.message || 'Erreur de connexion',
      errors: {},
      status: 0
    };
  }
}

// Instance par défaut
const subscriptionPlanService = new SubscriptionPlanService();
export default subscriptionPlanService;
